import { useState, useMemo, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import {
  IoArrowBackOutline,
  IoArrowForwardOutline,
  IoCameraOutline,
  IoVideocamOffOutline,
  IoCarOutline,
  IoTrashOutline,
  IoCheckmarkCircle,
  IoAlertCircleOutline,
  IoBarcodeOutline,
  IoTimeOutline,
  IoListOutline,
  IoCheckmarkDone,
} from 'react-icons/io5';
import { api, HttpError, type FbsPostingDetail } from '../api';
import { BarcodeScanner } from '../components/BarcodeScanner';
import { useAuth } from '../auth';
import { Button } from '../design';

type Step = 'verify' | 'scan' | 'confirm';

interface ScannedMark {
  product_id: number;
  raw: string;
}

export function ShipmentDetailPage() {
  const { postingNumber } = useParams<{ postingNumber: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';

  const posting = useQuery({
    queryKey: ['fbs-posting', postingNumber],
    queryFn: () => api.fbsPostingGet(postingNumber!),
    enabled: !!postingNumber,
  });

  if (posting.isLoading) return <div className="card">Загрузка отправления…</div>;
  if (posting.error) return <div className="card" style={{ color: 'var(--red)' }}>{(posting.error as Error).message}</div>;
  if (!posting.data) return null;

  return <Wizard posting={posting.data} isAdmin={isAdmin} onDone={() => navigate('/shipments')} />;
}

function Wizard({ posting, isAdmin, onDone }: { posting: FbsPostingDetail; isAdmin: boolean; onDone: () => void }) {
  const hasMarkRequired = posting.products.some((p) => p.mandatory_mark_required);
  // Шаги: verify → (scan если маркировка) → confirm (/ship) → возврат в список (этикетки печатаются оптом)
  const steps: { key: Step; label: string; skip?: boolean }[] = [
    { key: 'verify', label: 'Сверка состава' },
    { key: 'scan', label: 'Маркировка', skip: !hasMarkRequired },
    { key: 'confirm', label: 'Завершить сборку' },
  ];
  const activeSteps = steps.filter((s) => !s.skip);

  const [step, setStep] = useState<Step>('verify');
  const stepIdx = activeSteps.findIndex((s) => s.key === step);

  const [verified, setVerified] = useState<Set<number>>(new Set());
  const [scanned, setScanned] = useState<ScannedMark[]>([]);

  const allVerified = posting.products.every((p) => verified.has(p.product_id));
  const requiredMarkCount = useMemo(
    () => posting.products.filter((p) => p.mandatory_mark_required).reduce((s, p) => s + p.quantity, 0),
    [posting.products],
  );
  const allMarked = !hasMarkRequired || scanned.length === requiredMarkCount;

  const ship = useMutation({
    mutationFn: () => {
      const productMap = new Map<number, { product_id: number; quantity: number; exemplars: { mandatory_mark: string }[] }>();
      for (const p of posting.products) {
        productMap.set(p.product_id, { product_id: p.product_id, quantity: p.quantity, exemplars: [] });
      }
      for (const s of scanned) {
        productMap.get(s.product_id)?.exemplars.push({ mandatory_mark: s.raw });
      }
      return api.fbsShip(posting.posting_number, { packages: [{ products: Array.from(productMap.values()) }] });
    },
    // После /ship — возврат в список (этикетки печатаются оптом из общего workflow)
    onSuccess: () => setTimeout(onDone, 1500),
  });

  const goNext = () => {
    const next = activeSteps[stepIdx + 1];
    if (next) setStep(next.key);
  };
  const goPrev = () => {
    const prev = activeSteps[stepIdx - 1];
    if (prev) setStep(prev.key);
  };

  const deadline = posting.shipment_date ? new Date(posting.shipment_date) : null;
  const hoursLeft = deadline ? Math.round((deadline.getTime() - Date.now()) / 3600_000) : null;

  return (
    <>
      <div className="page-header">
        <div>
          <Link to="/shipments" className="btn" style={{ marginBottom: 8 }}>
            <IoArrowBackOutline /> К списку
          </Link>
          <h2 style={{ marginTop: 4 }}>{posting.posting_number}</h2>
          <div className="muted" style={{ fontSize: 13, marginTop: 4, display: 'flex', gap: 16, flexWrap: 'wrap' }}>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}><IoCarOutline /> {posting.delivery_method_name ?? 'без метода'}</span>
            {deadline && (
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, color: hoursLeft != null && hoursLeft < 4 ? 'var(--red)' : undefined }}>
                <IoTimeOutline /> до {deadline.toLocaleString('ru-RU')}
                {hoursLeft != null && ` (${hoursLeft > 0 ? `${hoursLeft}ч осталось` : 'просрочено'})`}
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Stepper */}
      <div className="stepper">
        {activeSteps.map((s, i) => {
          const state = i < stepIdx ? 'done' : i === stepIdx ? 'active' : 'pending';
          return (
            <div key={s.key} className={`stepper-step ${state}`}>
              <div className="stepper-circle">
                {state === 'done' ? <IoCheckmarkDone /> : i + 1}
              </div>
              <div className="stepper-label">{s.label}</div>
              {i < activeSteps.length - 1 && <div className={`stepper-line ${i < stepIdx ? 'done' : ''}`} />}
            </div>
          );
        })}
      </div>

      {/* Active step */}
      {step === 'verify' && (
        <VerifyStep posting={posting} verified={verified} onToggle={(pid) => {
          setVerified((prev) => {
            const next = new Set(prev);
            if (next.has(pid)) next.delete(pid); else next.add(pid);
            return next;
          });
        }} />
      )}

      {step === 'scan' && (
        <ScanStep posting={posting} scanned={scanned} setScanned={setScanned} />
      )}

      {step === 'confirm' && (
        <ConfirmStep
          posting={posting}
          scanned={scanned}
          isAdmin={isAdmin}
          onShip={() => ship.mutate()}
          shipState={ship}
        />
      )}

      {/* Navigation footer */}
      <div className="wizard-footer">
        <div>
          {stepIdx > 0 && (
            <Button variant="secondary" onClick={goPrev} disabled={ship.isPending} leftIcon={<IoArrowBackOutline />}>
              Назад
            </Button>
          )}
        </div>
        <div className="muted" style={{ fontSize: 13 }}>
          Шаг {stepIdx + 1} из {activeSteps.length}
        </div>
        <div>
          {step === 'verify' && (
            <Button variant="primary" onClick={goNext} disabled={!allVerified} rightIcon={<IoArrowForwardOutline />}>
              Дальше
            </Button>
          )}
          {step === 'scan' && (
            <Button variant="primary" onClick={goNext} disabled={!allMarked} rightIcon={<IoArrowForwardOutline />}>
              Дальше
            </Button>
          )}
        </div>
      </div>
    </>
  );
}

// ──────────────────────────────────────────────────────────────
// Шаг 1: Сверка состава
// ──────────────────────────────────────────────────────────────
function VerifyStep({ posting, verified, onToggle }: {
  posting: FbsPostingDetail;
  verified: Set<number>;
  onToggle: (productId: number) => void;
}) {
  const allVerified = posting.products.every((p) => verified.has(p.product_id));
  return (
    <div className="card" style={{ padding: 24 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <div>
          <h3 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: 8 }}>
            <IoListOutline /> Шаг 1. Соберите товары и проверьте состав
          </h3>
          <div className="muted" style={{ fontSize: 13, marginTop: 4 }}>
            Снимите товары со стеллажа, физически проверьте каждую позицию и отметьте галочкой.
          </div>
        </div>
        <div style={{ fontSize: 22, fontWeight: 700, color: allVerified ? 'var(--green)' : 'var(--accent)' }}>
          {verified.size}<span style={{ color: 'var(--text-muted)', fontWeight: 400, fontSize: 16 }}>/{posting.products.length}</span>
        </div>
      </div>

      <div className="verify-list">
        {posting.products.map((p) => {
          const checked = verified.has(p.product_id);
          return (
            <label key={p.product_id} className={`verify-item${checked ? ' checked' : ''}`}>
              <input type="checkbox" checked={checked} onChange={() => onToggle(p.product_id)} />
              <div className="verify-item-content">
                <div className="verify-item-name">{p.name}</div>
                <div className="verify-item-meta">
                  <code>{p.offer_id}</code>
                  <span>× <strong>{p.quantity}</strong> шт.</span>
                  {p.mandatory_mark_required && (
                    <span className="badge badge-warn">
                      <IoBarcodeOutline /> Маркировка
                    </span>
                  )}
                </div>
              </div>
              <div className="verify-item-check">
                {checked && <IoCheckmarkCircle style={{ color: 'var(--green)', fontSize: 24 }} />}
              </div>
            </label>
          );
        })}
      </div>

      {!allVerified && (
        <div className="muted" style={{ fontSize: 13, marginTop: 16, padding: '8px 12px', background: 'var(--accent-soft)', color: 'var(--accent-text)', borderRadius: 6 }}>
          Отметьте все позиции чтобы перейти к следующему шагу.
        </div>
      )}
    </div>
  );
}

// ──────────────────────────────────────────────────────────────
// Шаг 2: Сканирование маркировки
// ──────────────────────────────────────────────────────────────
function ScanStep({ posting, scanned, setScanned }: {
  posting: FbsPostingDetail;
  scanned: ScannedMark[];
  setScanned: (s: ScannedMark[] | ((prev: ScannedMark[]) => ScannedMark[])) => void;
}) {
  const markRequiredProducts = posting.products.filter((p) => p.mandatory_mark_required);

  // Автовыбор: первый неполностью отсканированный товар
  const firstIncomplete = markRequiredProducts.find(
    (p) => scanned.filter((s) => s.product_id === p.product_id).length < p.quantity,
  );
  const [activePid, setActivePid] = useState<number | null>(firstIncomplete?.product_id ?? markRequiredProducts[0]?.product_id ?? null);
  const [manualInput, setManualInput] = useState('');
  const [cameraOn, setCameraOn] = useState(false);
  const [feedback, setFeedback] = useState<{ kind: 'success' | 'error'; text: string } | null>(null);

  // Автопереход на следующий товар когда текущий завершён
  useEffect(() => {
    if (!activePid) return;
    const current = markRequiredProducts.find((p) => p.product_id === activePid);
    if (!current) return;
    const count = scanned.filter((s) => s.product_id === activePid).length;
    if (count >= current.quantity) {
      const next = markRequiredProducts.find(
        (p) => p.product_id !== activePid && scanned.filter((s) => s.product_id === p.product_id).length < p.quantity,
      );
      if (next) {
        setTimeout(() => setActivePid(next.product_id), 800);
      }
    }
  }, [scanned, activePid, markRequiredProducts]);

  const handleScan = (code: string) => {
    setFeedback(null);
    if (!activePid) { setFeedback({ kind: 'error', text: 'Выберите товар для сканирования' }); return; }
    if (scanned.find((s) => s.raw === code)) {
      setFeedback({ kind: 'error', text: 'Этот код уже отсканирован' });
      return;
    }
    const product = markRequiredProducts.find((p) => p.product_id === activePid);
    if (!product) return;
    const currentCount = scanned.filter((s) => s.product_id === activePid).length;
    if (currentCount >= product.quantity) {
      setFeedback({ kind: 'error', text: `Для «${product.name}» уже отсканировано ${currentCount} из ${product.quantity}` });
      return;
    }
    setScanned((prev) => [...prev, { product_id: activePid, raw: code }]);
    setFeedback({ kind: 'success', text: `${currentCount + 1} / ${product.quantity} для «${product.name}»` });
    setTimeout(() => setFeedback(null), 1500);
    setManualInput('');
  };

  const removeScan = (idx: number) => setScanned((prev) => prev.filter((_, i) => i !== idx));

  const totalRequired = markRequiredProducts.reduce((s, p) => s + p.quantity, 0);
  const active = markRequiredProducts.find((p) => p.product_id === activePid);
  const activeCount = active ? scanned.filter((s) => s.product_id === active.product_id).length : 0;

  return (
    <div className="card" style={{ padding: 24 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <div>
          <h3 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: 8 }}>
            <IoBarcodeOutline /> Шаг 2. Сканирование маркировки «Честный знак»
          </h3>
          <div className="muted" style={{ fontSize: 13, marginTop: 4 }}>
            Сканируйте Data Matrix на каждой единице товара. Камерой или USB-сканером.
          </div>
        </div>
        <div style={{ fontSize: 22, fontWeight: 700, color: scanned.length === totalRequired ? 'var(--green)' : 'var(--accent)' }}>
          {scanned.length}<span style={{ color: 'var(--text-muted)', fontWeight: 400, fontSize: 16 }}>/{totalRequired}</span>
        </div>
      </div>

      <div className="progress-bar" style={{ marginBottom: 24 }}>
        <div
          className="progress-bar-fill"
          style={{
            width: `${Math.min(100, (scanned.length / Math.max(1, totalRequired)) * 100)}%`,
            background: scanned.length === totalRequired ? 'var(--green)' : 'var(--accent)',
          }}
        />
      </div>

      {/* Селектор товаров — большие кликабельные карточки */}
      <div className="scan-product-list">
        {markRequiredProducts.map((p) => {
          const count = scanned.filter((s) => s.product_id === p.product_id).length;
          const done = count >= p.quantity;
          const isActive = p.product_id === activePid;
          return (
            <button
              key={p.product_id}
              className={`scan-product${isActive ? ' active' : ''}${done ? ' done' : ''}`}
              onClick={() => setActivePid(p.product_id)}
              type="button"
            >
              <div className="scan-product-name">{p.name}</div>
              <div className="scan-product-progress">
                {done ? <IoCheckmarkCircle style={{ color: 'var(--green)', fontSize: 18 }} /> : null}
                <strong>{count}</strong><span className="muted">/{p.quantity}</span>
              </div>
            </button>
          );
        })}
      </div>

      {active && (
        <div className="scan-active-area">
          <div className="scan-active-header">
            <div>
              <div className="muted" style={{ fontSize: 12 }}>Сейчас сканируем</div>
              <div style={{ fontSize: 16, fontWeight: 600 }}>{active.name}</div>
              <div className="muted" style={{ fontSize: 13, marginTop: 2 }}>
                Артикул <code>{active.offer_id}</code> · Прогресс: <strong>{activeCount}</strong> из {active.quantity}
              </div>
            </div>
            <Button variant="secondary" onClick={() => setCameraOn((v) => !v)} type="button" leftIcon={cameraOn ? <IoVideocamOffOutline /> : <IoCameraOutline />}>
              {cameraOn ? 'Выключить камеру' : 'Включить камеру'}
            </Button>
          </div>

          {cameraOn && <div style={{ marginBottom: 16 }}><BarcodeScanner onScan={handleScan} /></div>}

          <div>
            <div className="filter-field-label" style={{ marginBottom: 6 }}>Ручной ввод / USB-сканер (Enter — отправить)</div>
            <input
              className="input"
              autoFocus
              placeholder="Отсканируйте или введите код Data Matrix…"
              value={manualInput}
              onChange={(e) => setManualInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter' && manualInput.trim()) handleScan(manualInput.trim()); }}
              style={{ fontFamily: 'monospace', fontSize: 14, padding: '12px 14px' }}
            />
          </div>

          {feedback && (
            <div
              className={feedback.kind === 'success' ? 'feedback-ok' : 'feedback-err'}
              style={{ marginTop: 12 }}
            >
              {feedback.kind === 'success' ? <IoCheckmarkCircle /> : <IoAlertCircleOutline />}
              {feedback.text}
            </div>
          )}
        </div>
      )}

      {scanned.length > 0 && (
        <details style={{ marginTop: 20 }}>
          <summary style={{ cursor: 'pointer', fontSize: 13, fontWeight: 500 }}>
            Все отсканированные коды ({scanned.length})
          </summary>
          <table className="products" style={{ marginTop: 12 }}>
            <thead><tr><th style={{ width: 40 }}>#</th><th>Товар</th><th>Код</th><th style={{ width: 60 }}></th></tr></thead>
            <tbody>
              {scanned.map((s, i) => {
                const product = posting.products.find((pr) => pr.product_id === s.product_id);
                return (
                  <tr key={i}>
                    <td className="muted">{i + 1}</td>
                    <td style={{ fontSize: 12 }}>{product?.name ?? `id=${s.product_id}`}</td>
                    <td style={{ fontFamily: 'monospace', fontSize: 11, color: 'var(--text-muted)' }}>
                      {s.raw.slice(0, 60)}{s.raw.length > 60 && '…'}
                    </td>
                    <td>
                      <Button variant="secondary" onClick={() => removeScan(i)} title="Удалить скан" type="button" iconOnly aria-label="Удалить скан">
                        <IoTrashOutline />
                      </Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </details>
      )}
    </div>
  );
}


// ──────────────────────────────────────────────────────────────
// Шаг 3: Завершить сборку — вызов /v4/posting/fbs/ship
// ──────────────────────────────────────────────────────────────
function ConfirmStep({ posting, scanned, isAdmin, onShip, shipState }: {
  posting: FbsPostingDetail;
  scanned: ScannedMark[];
  isAdmin: boolean;
  onShip: () => void;
  shipState: { isPending: boolean; isSuccess: boolean; data?: { shipment_id: string }; error?: unknown };
}) {
  const totalItems = posting.products.reduce((s, p) => s + p.quantity, 0);

  return (
    <div className="card" style={{ padding: 24 }}>
      <h3 style={{ marginTop: 0, marginBottom: 16, display: 'flex', alignItems: 'center', gap: 8 }}>
        <IoCheckmarkDone /> Шаг 3. Завершить сборку
      </h3>

      <div className="confirm-summary">
        <div className="confirm-summary-item">
          <div className="muted" style={{ fontSize: 12 }}>Отправление</div>
          <div style={{ fontSize: 15, fontWeight: 600 }}>{posting.posting_number}</div>
        </div>
        <div className="confirm-summary-item">
          <div className="muted" style={{ fontSize: 12 }}>Доставка</div>
          <div style={{ fontSize: 14 }}>{posting.delivery_method_name ?? '—'}</div>
        </div>
        <div className="confirm-summary-item">
          <div className="muted" style={{ fontSize: 12 }}>Товаров</div>
          <div style={{ fontSize: 15, fontWeight: 600 }}>{totalItems} ед. в {posting.products.length} позициях</div>
        </div>
        <div className="confirm-summary-item">
          <div className="muted" style={{ fontSize: 12 }}>Кодов маркировки</div>
          <div style={{ fontSize: 15, fontWeight: 600 }}>{scanned.length}</div>
        </div>
      </div>

      <div style={{ marginTop: 24, padding: 16, background: 'var(--accent-soft)', borderRadius: 8, color: 'var(--accent-text)', fontSize: 13 }}>
        После нажатия данные уйдут в Ozon: коды маркировки записываются в Честный знак, статус становится «awaiting_deliver».
        Затем сразу откроется шаг с этикеткой PDF.
      </div>

      <div style={{ marginTop: 20 }}>
        {isAdmin ? (
          <Button
            variant="primary"
            disabled={shipState.isSuccess}
            loading={shipState.isPending}
            onClick={onShip}
            style={{ fontSize: 16, padding: '14px 32px' }}
            leftIcon={<IoCarOutline />}
          >
            {shipState.isSuccess ? 'Сборка завершена' : 'Завершить сборку'}
          </Button>
        ) : (
          <div className="muted">Только администратор может завершить сборку.</div>
        )}

        {shipState.isSuccess && shipState.data && (
          <div className="feedback-ok" style={{ marginTop: 16 }}>
            <IoCheckmarkCircle />
            Готово! shipment_id: <code style={{ marginLeft: 4 }}>{shipState.data.shipment_id}</code>. Переход к этикетке…
          </div>
        )}
        {shipState.error != null && (
          <div className="feedback-err" style={{ marginTop: 16 }}>
            <IoAlertCircleOutline />
            {shipState.error instanceof HttpError ? `Ошибка ${shipState.error.status}: ${JSON.stringify(shipState.error.payload)}` : (shipState.error as Error).message}
          </div>
        )}
      </div>
    </div>
  );
}
